// src/components/WidgetOportunidadesEscondidas.js
// Sessão 4.9 — Card no Dashboard para descoberta de negócio latente.
//
// Mostra empresas que conversam por email mas não têm oferta cadastrada.
// Dois CTAs por linha:
//   📧 Ver emails  → abre EmailsHubModal modo=empresa (reúso da Camada 1)
//   ➕ Criar oferta → dispara callback onCriarOferta(empresaId, empresaNome)
//                    (pai decide como abrir CreateOfertaModal pré-preenchido)
//
// Regras respeitadas:
//   - Regra 6 (hooks antes de return): todos os hooks no topo.
//   - Regra 7 (arquivos novos, não reescrever): componente isolado.
//   - Regra 10 (não tocar em HITL): só leitura.

import React, { useState } from 'react';
import { Lightbulb, Mail, PlusCircle, RefreshCw, AlertCircle } from 'lucide-react';
import { useOportunidadesEscondidas } from '../hooks/useOportunidadesEscondidas';
import EmailsHubModal from './EmailsHubModal';

const formatDias = (dias) => {
  if (dias === null || dias === undefined) return '';
  if (dias === 0) return 'hoje';
  if (dias === 1) return 'há 1 dia';
  if (dias < 30) return `há ${dias} dias`;
  if (dias < 60) return 'há 1 mês';
  if (dias < 365) return `há ${Math.floor(dias / 30)} meses`;
  return `há ${Math.floor(dias / 365)} ano(s)`;
};

// Cor do indicador por idade (quanto mais velho, mais alerta)
const corPorIdade = (dias) => {
  if (dias === null) return 'text-slate-400';
  if (dias <= 30) return 'text-emerald-600';
  if (dias <= 90) return 'text-amber-600';
  return 'text-red-600';
};

const WidgetOportunidadesEscondidas = ({ onCriarOferta, onOpenEmail, minEmails = 2, maxItens = 10 }) => {
  // === Hooks antes de qualquer early return (Regra 6) ===
  const { oportunidades, loading, erro, refresh } = useOportunidadesEscondidas({ minEmails });
  const [empresaModalEmails, setEmpresaModalEmails] = useState(null);

  const lista = oportunidades.slice(0, maxItens);
  const totalOculto = Math.max(0, oportunidades.length - maxItens);

  return (
    <div className="bg-white rounded-lg shadow border border-slate-200 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="flex items-center gap-2 font-semibold text-slate-800">
          <Lightbulb size={18} className="text-amber-500" />
          Oportunidades escondidas
          {!loading && oportunidades.length > 0 && (
            <span className="text-xs font-normal bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
              {oportunidades.length}
            </span>
          )}
        </h3>
        <button
          onClick={refresh}
          className="text-slate-400 hover:text-slate-600 p-1"
          title="Atualizar"
          disabled={loading}
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <p className="text-xs text-slate-500 mb-3">
        Empresas com ≥ {minEmails} emails, sem oferta cadastrada.
      </p>

      {/* Estados */}
      {loading && (
        <div className="text-sm text-slate-500 py-4 text-center">Carregando...</div>
      )}

      {!loading && erro && (
        <div className="text-sm text-red-600 bg-red-50 rounded p-2 flex items-start gap-2">
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
          <span>Erro ao carregar: {erro.message || String(erro)}</span>
        </div>
      )}

      {!loading && !erro && oportunidades.length === 0 && (
        <div className="text-sm text-slate-500 italic py-4 text-center bg-slate-50 rounded">
          Nenhuma oportunidade escondida no momento.
        </div>
      )}

      {/* Lista */}
      {!loading && !erro && lista.length > 0 && (
        <ul className="divide-y divide-slate-100">
          {lista.map((op) => (
            <li key={op.empresa_id} className="flex items-center justify-between gap-2 py-2">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-slate-800 truncate" title={op.empresa_nome}>
                  {op.empresa_nome}
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-1 flex-wrap">
                  <span>{op.qtd_emails} emails</span>
                  <span>·</span>
                  <span>{op.qtd_contatos} {op.qtd_contatos === 1 ? 'contato' : 'contatos'}</span>
                  {op.dias_desde_ultimo !== null && (
                    <>
                      <span>·</span>
                      <span className={corPorIdade(op.dias_desde_ultimo)}>
                        {formatDias(op.dias_desde_ultimo)}
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className="flex gap-1 flex-shrink-0">
                <button
                  onClick={() =>
                    setEmpresaModalEmails({ id: op.empresa_id, nome: op.empresa_nome })
                  }
                  className="text-xs px-2 py-1.5 bg-slate-100 text-slate-700 rounded hover:bg-slate-200 flex items-center gap-1"
                  title="Ver todos os emails desta empresa"
                >
                  <Mail size={12} /> Emails
                </button>
                {onCriarOferta && (
                  <button
                    onClick={() => onCriarOferta(op.empresa_id, op.empresa_nome)}
                    className="text-xs px-2 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 flex items-center gap-1"
                    title="Criar oferta com esta empresa"
                  >
                    <PlusCircle size={12} /> Oferta
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {totalOculto > 0 && (
        <div className="text-xs text-slate-400 italic text-center pt-2">
          + {totalOculto} oportunidade(s) não exibida(s)
        </div>
      )}

      {/* Modal de emails da empresa selecionada */}
      {empresaModalEmails && (
        <EmailsHubModal
          modo="empresa"
          empresaId={empresaModalEmails.id}
          empresaNome={empresaModalEmails.nome}
          onClose={() => setEmpresaModalEmails(null)}
          onOpenEmail={(em) => { setEmpresaModalEmails(null); if (onOpenEmail) onOpenEmail(em); }}
          onOpenOferta={(o) => console.log('[WidgetOport] abrir oferta', o?.id)}
        />
      )}
    </div>
  );
};

export default WidgetOportunidadesEscondidas;
